import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes
} from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/cn'

const CONTROL =
  'w-full rounded-md border border-line bg-raised px-3 text-[12.5px] text-fg transition-colors placeholder:text-subtle hover:border-line-strong focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/40 disabled:cursor-not-allowed disabled:text-subtle disabled:opacity-70'

export interface FieldProps {
  label?: string
  hint?: string
  error?: string
  className?: string
  children: (id: string) => ReactNode
}

/** Label + control + hint, wired together for screen readers. */
export function Field({ label, hint, error, className, children }: FieldProps): ReactNode {
  const id = useId()
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label htmlFor={id} className="label-caps block text-subtle">
          {label}
        </label>
      )}
      {children(id)}
      {error ? (
        <p className="text-[11.5px] text-negative">{error}</p>
      ) : (
        hint && <p className="text-[11.5px] text-muted">{hint}</p>
      )}
    </div>
  )
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: LucideIcon
  /** Room for a button parked inside the control, e.g. a reveal toggle. */
  trailing?: ReactNode
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { icon: Icon, trailing, className, ...rest },
  ref
) {
  return (
    <div className="relative">
      {Icon && (
        <Icon
          size={14}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle"
          aria-hidden
        />
      )}
      <input
        ref={ref}
        className={cn(CONTROL, 'h-9', Icon && 'pl-9', Boolean(trailing) && 'pr-10', className)}
        {...rest}
      />
      {trailing && <div className="absolute right-1 top-1/2 -translate-y-1/2">{trailing}</div>}
    </div>
  )
})

export type SelectInputProps = SelectHTMLAttributes<HTMLSelectElement>

export const SelectInput = forwardRef<HTMLSelectElement, SelectInputProps>(function SelectInput(
  { className, children, ...rest },
  ref
) {
  return (
    <select ref={ref} className={cn(CONTROL, 'h-9 cursor-pointer pr-8', className)} {...rest}>
      {children}
    </select>
  )
})

export interface SwitchProps {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  description?: string
  disabled?: boolean
}

export function Switch({ checked, onChange, label, description, disabled }: SwitchProps): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between gap-4 text-left disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span className="min-w-0">
        <span className="block text-[12.5px] font-medium text-fg">{label}</span>
        {description && <span className="block text-[11.5px] text-muted">{description}</span>}
      </span>
      <span
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-full transition-colors',
          checked ? 'bg-accent' : 'bg-line-strong'
        )}
      >
        <span
          className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform"
          style={{ transform: checked ? 'translateX(16px)' : 'translateX(0)' }}
        />
      </span>
    </button>
  )
}
